<script setup lang="ts">
    import { computed, onMounted, onUnmounted, ref } from 'vue';
    import { caseIdFromRoute, ROUTES } from '@/router';
    import CatStackContainer from '@/views/dicoms/ctScan/CatStackContainer.vue';
    import OverlayViewer from '@/views/dicoms/overlay/OverlayViewer.vue';
    import XRaysView from '@/views/dicoms/xrays/XRaysView.vue';
    import { useCaseSyncStore } from '@/stores/useCaseSyncStore';
    import { useUserStore } from '@/stores/userStore';
    import { useScanViewStore } from '@/views/dicoms/scanViewStore';
    import { useRoute } from 'vue-router';

    const caseStore = useCaseSyncStore();
    const userStore = useUserStore();
    const viewStore = useScanViewStore();
    const route = useRoute();

    const activeTab = ref('loading');

    const showCtScansTab = computed(() => viewStore.modality === 'ct-scan');
    const showOverlayTab = computed(() => {
        return (
            viewStore.modality === 'ct-scan' &&
            (userStore.isSurgicalSupportEngineer || userStore.isAdmin) &&
            caseStore.isSegmentationComplete
        );
    });
    const showXraysTab = computed(() => viewStore.modality === 'xray');

    onMounted(async () => {
        await viewStore.loadCase(caseIdFromRoute(route));

        switch (viewStore.modality) {
            case 'ct-scan':
                activeTab.value = 'ct-scans';
                break;
            case 'xray':
                activeTab.value = 'xrays';
                break;
            default:
                activeTab.value = 'loading';
        }
    });

    onUnmounted(() => {
        viewStore.stop();
    });
</script>

<template>
    <v-layout>
        <app-sidebar>
            <router-link :to="{ name: ROUTES.HOME }">
                <app-link icon="mdi-arrow-left">Back to your Cases</app-link>
            </router-link>
        </app-sidebar>

        <app-main-view v-if="showCtScansTab || showOverlayTab">
            <app-form-title>Dicoms</app-form-title>

            <app-main-card outlined height="100%" class="scans-card">
                <v-card-title class="scan-tab-title">
                    <v-tabs v-model="activeTab" class="scan-tab" align-tabs="start">
                        <v-tab v-if="showCtScansTab" value="ct-scans">
                            CT Scans
                        </v-tab>
                        <v-tab v-if="showOverlayTab" value="segmentation-overlay">
                            Segmentation Overlay
                        </v-tab>
                    </v-tabs>
                </v-card-title>

                <v-card-text class="pa-0">
                    <v-window v-model="activeTab" class="scan-window">
                        <v-window-item value="loading">
                            <v-progress-circular indeterminate />
                            Loading...
                        </v-window-item>
                        <v-window-item value="ct-scans">
                            <cat-stack-container />
                        </v-window-item>
                        <v-window-item value="segmentation-overlay">
                            <overlay-viewer />
                        </v-window-item>
                    </v-window>
                </v-card-text>
            </app-main-card>
        </app-main-view>

        <v-main v-else class="app-main-view">
            <div class="d-flex flex-column fill-height">
                <app-form-title>Dicoms</app-form-title>
                <v-sheet outlined class="d-flex flex-column flex-grow-1 xrays-sheet">
                    <v-card-title class="scan-tab-title">
                        <v-tabs v-model="activeTab" class="scan-tab scan-tab-title" align-tabs="start">
                            <v-tab v-if="showXraysTab" value="xrays">X-Rays</v-tab>
                        </v-tabs>
                    </v-card-title>
                    <div
                        v-if="activeTab === 'loading'"
                        class="d-flex flex-column flex-grow-1 align-center justify-center ga-4">
                        <v-progress-circular indeterminate size="40" color="primary" />
                        Loading X-Rays...
                    </div>
                    <div v-if="activeTab === 'xrays'" class="d-flex flex-column flex-grow-1">
                        <x-rays-view class="flex-grow-1 ma-5" />
                    </div>
                </v-sheet>
            </div>
        </v-main>
    </v-layout>
</template>

<style scoped lang="scss">
    .app-main-view {
        margin-top: 2.5rem;

        //push the main content to the right to leave space for the sticky sidebar
        --v-layout-left: 260px !important;
    }

    .scans-card {
        max-width: unset !important;
        width: fit-content;
        margin: 0;
        align-self: flex-start;
        display: flex;
        flex-direction: column;
    }

    .xrays-sheet {
        border-radius: 12px;
        border: 1px solid rgb(var(--v-theme-grey-light-1));
        margin: 0 20px 20px 0;
    }

    .scan-tab-title {
        padding: 0 !important;

        .scan-tab {
            :deep(.v-tab) {
                text-transform: capitalize;
                font-size: 18px;
                max-width: none;
                gap: 20px;

                &.v-tab--selected {
                    color: rgb(var(--v-theme-primary));

                    .v-tab__slider {
                        height: 2px;
                        background-color: rgb(var(--v-theme-primary));
                    }
                }
            }
        }
    }
</style>
